import IndexedDBService from './indexedDBService';

/**
 * Store Database Service
 * Manages store data in IndexedDB (offline-first)
 */
class StoreDB extends IndexedDBService {
  constructor() {
    super('storeDB', 2); // Incremented version for new startingCash store
  }

  protected setupObjectStores(db: IDBDatabase): void {
    // Store Station ID Store
    if (!db.objectStoreNames.contains('storeStationIds')) {
      const storeStationIdsStore = db.createObjectStore('storeStationIds', {
        keyPath: 'storeId',
      });
      storeStationIdsStore.createIndex('storeId', 'storeId', { unique: true });
      storeStationIdsStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    // Store Settings Store (for offline-first store settings)
    if (!db.objectStoreNames.contains('storeSettings')) {
      const storeSettingsStore = db.createObjectStore('storeSettings', {
        keyPath: 'storeId',
      });
      storeSettingsStore.createIndex('storeId', 'storeId', { unique: true });
      storeSettingsStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    // EOD Records Store (for offline-first EOD completion records)
    if (!db.objectStoreNames.contains('eodRecords')) {
      const eodRecordsStore = db.createObjectStore('eodRecords', {
        keyPath: 'id',
      });
      eodRecordsStore.createIndex('storeId', 'storeId', { unique: false });
      eodRecordsStore.createIndex('date', 'date', { unique: false });
      eodRecordsStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    // Starting Cash Store (for daily starting cash balance)
    if (!db.objectStoreNames.contains('startingCash')) {
      const startingCashStore = db.createObjectStore('startingCash', {
        keyPath: 'id',
      });
      startingCashStore.createIndex('storeId', 'storeId', { unique: false });
      startingCashStore.createIndex('date', 'date', { unique: false });
      startingCashStore.createIndex('timestamp', 'timestamp', { unique: false });
    }
  }

  /**
   * Save store station ID
   */
  async saveStationId(storeId: string, stationId: string, stationName: string, stationNameHistory: Array<{ name: string; changedAt: number }> = [], assigned = false): Promise<void> {
    const existing = await this.getStationId(storeId).catch(() => null);
    await this.put('storeStationIds', {
      storeId,
      stationId: stationId || existing?.stationId || "",
      stationName,
      stationNameHistory,
      deviceId: existing?.deviceId || "",
      assigned: assigned === true || existing?.assigned === true,
      timestamp: Date.now(),
    });
  }

  /**
   * Get store station ID
   */
  async getStationId(storeId: string): Promise<any> {
    return await this.get('storeStationIds', storeId);
  }

  async saveWorkstationDevice(storeId: string, deviceId: string): Promise<void> {
    const existing = await this.getStationId(storeId).catch(() => null);
    await this.put("storeStationIds", {
      storeId,
      stationId: existing?.stationId || "",
      stationName: existing?.stationName || "",
      stationNameHistory: Array.isArray(existing?.stationNameHistory) ? existing.stationNameHistory : [],
      assigned: existing?.assigned === true,
      deviceId,
      timestamp: Date.now(),
    });
  }

  async clearAssignedStationId(storeId: string): Promise<void> {
    const existing = await this.getStationId(storeId).catch(() => null);
    if (!existing) return;
    await this.put('storeStationIds', {
      ...existing,
      stationId: "",
      assigned: false,
      timestamp: Date.now(),
    });
  }

  /**
   * Save store setting (offline-first)
   */
  async saveStoreSetting(storeId: string, settingKey: string, settingValue: any): Promise<void> {
    // Get existing settings or create new
    let settings: any = {};
    try {
      const existing = await this.get('storeSettings', storeId);
      if (existing) {
        settings = existing.settings || {};
      }
    } catch (error) {
      // No existing settings, start fresh
    }

    // Update the specific setting
    settings[settingKey] = settingValue;

    // Save to IndexedDB
    await this.put('storeSettings', {
      storeId,
      settings,
      timestamp: Date.now(),
    });
  }

  /**
   * Get store setting (offline-first)
   */
  async getStoreSetting(storeId: string, settingKey: string): Promise<any> {
    try {
      const data = await this.get('storeSettings', storeId);
      if (data && data.settings) {
        return data.settings[settingKey];
      }
    } catch (error) {
      // No settings found
    }
    return undefined;
  }

  /**
   * Get all store settings (offline-first)
   */
  async getAllStoreSettings(storeId: string): Promise<any> {
    try {
      const data = await this.get('storeSettings', storeId);
      if (data && data.settings) {
        return data.settings;
      }
    } catch (error) {
      // No settings found
    }
    return {};
  }

  /**
   * Save EOD record (offline-first)
   */
  async saveEODRecord(storeId: string, date: string, eodData: any): Promise<string> {
    const record = {
      storeId,
      date,
      ...eodData,
      timestamp: Date.now(),
      synced: false, // Mark as not synced to Firestore
    };
    
    // Use composite key: storeId_date
    const compositeKey = `${storeId}_${date}`;
    const recordWithKey = {
      id: compositeKey,
      ...record,
    };
    
    await this.put('eodRecords', recordWithKey);
    return compositeKey;
  }

  /**
   * Get EOD record by date (offline-first)
   */
  async getEODRecord(storeId: string, date: string): Promise<any> {
    try {
      const records = await this.getAll('eodRecords');
      return records.find((r: any) => r.storeId === storeId && r.date === date);
    } catch (error) {
      return null;
    }
  }

  /**
   * Get all EOD records for a store (offline-first)
   */
  async getAllEODRecords(storeId: string): Promise<any[]> {
    try {
      const records = await this.getAll('eodRecords');
      return records.filter((r: any) => r.storeId === storeId);
    } catch (error) {
      return [];
    }
  }

  /**
   * Mark EOD record as synced
   */
  async markEODRecordSynced(storeId: string, date: string): Promise<void> {
    try {
      const compositeKey = `${storeId}_${date}`;
      const record = await this.get('eodRecords', compositeKey);
      if (record) {
        await this.put('eodRecords', {
          ...record,
          synced: true,
        });
      }
    } catch (error) {
      console.error('Error marking EOD record as synced:', error);
    }
  }

  /**
   * Get unsynced EOD records (for background sync)
   */
  async getUnsyncedEODRecords(): Promise<any[]> {
    try {
      const records = await this.getAll('eodRecords');
      return records.filter((r: any) => !r.synced);
    } catch (error) {
      return [];
    }
  }

  /**
   * Save starting cash balance for a day (offline-first)
   */
  async saveStartingCash(storeId: string, date: string, amount: number): Promise<string> {
    const record = {
      storeId,
      date,
      amount,
      timestamp: Date.now(),
      synced: false, // Mark as not synced to Firestore
    };
    
    // Use composite key: storeId_date
    const compositeKey = `${storeId}_${date}`;
    const recordWithKey = {
      id: compositeKey,
      ...record,
    };
    
    await this.put('startingCash', recordWithKey);
    return compositeKey;
  }

  /**
   * Get starting cash balance for a day (offline-first)
   */
  async getStartingCash(storeId: string, date: string): Promise<any> {
    try {
      const compositeKey = `${storeId}_${date}`;
      return await this.get('startingCash', compositeKey);
    } catch (error) {
      return null;
    }
  }

  /**
   * Get all starting cash records for a store (offline-first)
   */
  async getAllStartingCash(storeId: string): Promise<any[]> {
    try {
      const records = await this.getAll('startingCash');
      return records.filter((r: any) => r.storeId === storeId);
    } catch (error) {
      return [];
    }
  }

  /**
   * Mark starting cash record as synced
   */
  async markStartingCashSynced(storeId: string, date: string): Promise<void> {
    try {
      const compositeKey = `${storeId}_${date}`;
      const record = await this.get('startingCash', compositeKey);
      if (record) {
        await this.put('startingCash', {
          ...record,
          synced: true,
        });
      }
    } catch (error) {
      console.error('Error marking starting cash as synced:', error);
    }
  }

  /**
   * Get unsynced starting cash records (for background sync)
   */
  async getUnsyncedStartingCash(): Promise<any[]> {
    try {
      const records = await this.getAll('startingCash');
      return records.filter((r: any) => !r.synced);
    } catch (error) {
      return [];
    }
  }
}

const storeDB = new StoreDB();
export default storeDB;

